import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import SearchableSelect from '../../searchable-select';

/** Maps condition type keys to display labels for the card header. */
const CONDITION_TYPE_DISPLAY = {
  portfolio_stage_reached: 'PORTFOLIO STAGE REACHED',
  cadence_qualification: 'QUALIFIES FOR A 2.0 CADENCE',
  saved_search: 'QUALIFIES FOR A SAVED SEARCH',
  list_membership: 'ADDED TO A LIST',
};

/**
 * A single madlib-style exit condition card.
 * Renders a sentence with inline inputs that configure the removal condition.
 */
const ExitConditionCard = ({
  condition,
  onChange,
  onRemove,
  portfolioTeams = {},
  cadences = [],
  savedSearches = [],
  contactLists = [],
}) => {
  const { type, config = {} } = condition;
  const [stages, setStages] = useState([]);

  // Load stages when team changes for portfolio_stage_reached
  useEffect(() => {
    if (type === 'portfolio_stage_reached' && config.teamId && portfolioTeams.getStages) {
      portfolioTeams.getStages(config.teamId).then((s) => setStages(s || []));
    } else {
      setStages([]);
    }
  }, [type, config.teamId, portfolioTeams]);

  const renderBody = () => {
    switch (type) {
      case 'portfolio_stage_reached':
        return (
          <p className="exit-condition-card--sentence">
            If the constituent moves into{' '}
            <select
              className="exit-condition-card--inline-select"
              value={String(config.teamId || '')}
              onChange={(e) => {
                const team = (portfolioTeams.teams || []).find((t) => String(t.id) === e.target.value);
                onChange({
                  ...condition,
                  config: {
                    ...config,
                    teamId: e.target.value || null,
                    teamName: team?.name || null,
                    stageId: null,
                    stageName: null,
                  },
                });
              }}
              aria-label="Portfolio team"
            >
              <option value="">pick a team</option>
              {(portfolioTeams.teams || []).map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
            &apos;s{' '}
            <select
              className="exit-condition-card--inline-select"
              value={String(config.stageId || '')}
              onChange={(e) => {
                const stage = stages.find((s) => String(s.id) === e.target.value);
                onChange({
                  ...condition,
                  config: { ...config, stageId: e.target.value || null, stageName: stage?.name || null },
                });
              }}
              disabled={!config.teamId}
              aria-label="Portfolio stage"
            >
              <option value="">pick a stage</option>
              {stages.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
            , the constituent will be automatically removed from this cadence.
          </p>
        );

      case 'cadence_qualification':
        return (
          <div className="exit-condition-card--sentence-block">
            <p className="exit-condition-card--sentence">
              If the constituent qualifies for the cadence below, the constituent will be automatically removed from
              this cadence.
            </p>
            <SearchableSelect
              options={cadences}
              value={cadences.find((c) => c.value === config.cadenceId) || null}
              onChange={(option) => {
                onChange({
                  ...condition,
                  config: { ...config, cadenceId: option?.value || null, cadenceName: option?.label || null },
                });
              }}
              placeholder="Search cadences..."
              className="exit-condition-card--searchable-dropdown"
            />
          </div>
        );

      case 'saved_search':
        return (
          <div className="exit-condition-card--sentence-block">
            <p className="exit-condition-card--sentence">
              If the constituent qualifies for the saved search below, the constituent will be automatically removed
              from this cadence.
            </p>
            <SearchableSelect
              options={savedSearches}
              value={savedSearches.find((s) => s.value === config.savedSearchId) || null}
              onChange={(option) => {
                onChange({
                  ...condition,
                  config: {
                    ...config,
                    savedSearchId: option?.value || null,
                    savedSearchName: option?.label || null,
                    savedSearchListId: option?.listId ?? null,
                  },
                });
              }}
              placeholder="Search saved searches..."
              className="exit-condition-card--searchable-dropdown"
            />
          </div>
        );

      case 'list_membership':
        return (
          <div className="exit-condition-card--sentence-block">
            <p className="exit-condition-card--sentence">
              If the constituent is added to the list below, the constituent will be automatically removed from this
              cadence.
            </p>
            <SearchableSelect
              options={contactLists}
              value={contactLists.find((l) => l.value === config.listId) || null}
              onChange={(option) => {
                onChange({
                  ...condition,
                  config: { ...config, listId: option?.value || null, listName: option?.label || null },
                });
              }}
              placeholder="Search lists..."
              className="exit-condition-card--searchable-dropdown"
            />
          </div>
        );

      default:
        return <p className="exit-condition-card--sentence">Unknown condition type: {type}</p>;
    }
  };

  return (
    <div className="exit-condition-card">
      <div className="exit-condition-card--header">
        <span className="exit-condition-card--type-label">{CONDITION_TYPE_DISPLAY[type] || type}</span>
        <button type="button" className="exit-condition-card--remove" onClick={onRemove} aria-label="Remove condition">
          <Icon icon="close" />
        </button>
      </div>
      <div className="exit-condition-card--body">{renderBody()}</div>
    </div>
  );
};

ExitConditionCard.propTypes = {
  condition: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    config: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  portfolioTeams: PropTypes.object,
  cadences: PropTypes.array,
  savedSearches: PropTypes.array,
  contactLists: PropTypes.array,
};

export default ExitConditionCard;
